import type { Channel } from '@/features/transactions/types'

/**
 * AF-126 — Alerts queue + detail data shapes.
 *
 * The wire format follows the backend AF-111 alerts API (alert summary in the
 * list endpoint, full detail with `notes`, `status_history`, and embedded
 * transaction in the detail endpoint). Mutation payloads are typed
 * separately so we can derive optimistic-update shapes cleanly.
 */

export type Priority = 'critical' | 'high' | 'medium' | 'low'

// Mirrors entities/alert.go's AlertStatus, plus the collapsed
// wire-only "closed" value the backend emits (uiAlertStatus in
// internal/api/alerts_handler.go buckets every closed_* + merged
// down to "closed" — callers that need the granular reason read
// `closed_resolution` instead). The granular `closed_*` variants
// are still accepted on the filter side (e.g. ?status=closed_fraud)
// and survive in mock fixtures, so they stay in the union.
export type AlertStatus =
  | 'open'
  | 'in_review'
  | 'escalated'
  | 'merged'
  | 'closed'
  | 'closed_fraud'
  | 'closed_false_positive'
  | 'closed_duplicate'
  | 'closed_other'

export const ALERT_CLOSED_STATUSES: readonly AlertStatus[] = [
  'closed',
  'closed_fraud',
  'closed_false_positive',
  'closed_duplicate',
  'closed_other',
] as const

export function isClosedStatus(s: AlertStatus): boolean {
  return (
    s === 'closed' ||
    s === 'closed_fraud' ||
    s === 'closed_false_positive' ||
    s === 'closed_duplicate' ||
    s === 'closed_other'
  )
}

// "merged" is terminal but not closed — alert lives inside its case
// envelope. Mirrors AlertStatus.IsTerminal in entities/alert.go.
export function isTerminalStatus(s: AlertStatus): boolean {
  return s === 'merged' || isClosedStatus(s)
}

/**
 * Human-readable label per `AlertStatus`. Single source of truth so
 * `StatusHistoryTimeline` and the bulk-close rejection summary (see
 * `describeBulkCloseRejection` below) never drift apart on wording.
 */
export const ALERT_STATUS_LABEL: Record<AlertStatus, string> = {
  open: 'Opened',
  in_review: 'In review',
  escalated: 'Escalated',
  merged: 'Merged into case',
  // Backend's uiAlertStatus collapses every closed_* + merged down
  // to "closed" on the wire; the granular labels stay for mock /
  // filter callsites.
  closed: 'Closed',
  closed_fraud: 'Closed — fraud confirmed',
  closed_false_positive: 'Closed — false positive',
  closed_duplicate: 'Closed — duplicate',
  closed_other: 'Closed',
}

export type Resolution =
  | 'false_positive'
  | 'fraud_confirmed'
  | 'insufficient_evidence'
  | 'duplicate'
  | 'customer_confirmed_legitimate'
  | 'other'

/**
 * Resolutions with no backend `closed_*` terminal yet (see
 * `entities.AlertStatus` in `internal/entities/alert.go` — it only
 * defines closed_fraud / closed_false_positive / closed_duplicate /
 * closed_other). Product has not decided how
 * `insufficient_evidence` and `customer_confirmed_legitimate` should
 * be represented server-side, so they stay in the `Resolution` union
 * (the detail page's `resolution` field can still read them back from
 * historical/mock data) but must never be offered as a live close
 * choice or sent over the wire. `CloseDialog` disables them; guard
 * new callers with `isDeferredResolution` before calling
 * `closedStatusForResolution`.
 */
export const DEFERRED_RESOLUTIONS: readonly Resolution[] = [
  'insufficient_evidence',
  'customer_confirmed_legitimate',
]

export function isDeferredResolution(r: Resolution): boolean {
  return (DEFERRED_RESOLUTIONS as readonly string[]).includes(r)
}

/**
 * Single source of truth for the UI `Resolution` → backend `closed_*`
 * `AlertStatus` translation. Both `api.ts` (real requests) and
 * `mocks.ts` (MSW fixtures) import this so they can never drift back
 * apart — the drift (mocks.ts had its own private copy of this
 * mapping while api.ts sent the raw `Resolution` value verbatim) is
 * exactly what let every real alert-close request 400 while every
 * mocked test kept passing.
 *
 * Throws for deferred resolutions — those have no backend terminal
 * and must never reach the wire. Callers that let an analyst pick a
 * resolution must disable the deferred options up front
 * (`isDeferredResolution`); this throw is the defense-in-depth
 * backstop, not the primary guard.
 */
export function closedStatusForResolution(r: Resolution): AlertStatus {
  switch (r) {
    case 'fraud_confirmed':
      return 'closed_fraud'
    case 'false_positive':
      return 'closed_false_positive'
    case 'duplicate':
      return 'closed_duplicate'
    case 'other':
      return 'closed_other'
    case 'insufficient_evidence':
    case 'customer_confirmed_legitimate':
      throw new Error(
        `Resolution "${r}" has no backend closed_* terminal yet and cannot be submitted. ` +
          'This resolution is deferred pending a product decision — the UI must not offer it as a close option.',
      )
  }
}

export interface Assignee {
  user_id: string
  display_name: string
  /** Initials precomputed by backend so the avatar never has to derive them. */
  initials: string
}

export interface CustomerSnapshot {
  id: string
  customer_number: string
  display_name: string
  /** 0–100. */
  risk_score: number | null
  /** Last 30d snapshot for the sparkline. */
  risk_score_history: number[]
  kyc_tier: 'tier_1' | 'tier_2' | 'tier_3' | 'unverified'
  pep: boolean
  bvn_masked: string | null
}

export interface SlaInfo {
  /** ISO timestamp the SLA started counting from. */
  started_at: string
  /** ISO timestamp the SLA expires (or expired). */
  deadline_at: string
  /** Configured target in minutes — used to render progress percentage. */
  target_minutes: number
}

export interface AlertSummary {
  id: string
  alert_number: string
  scenario_code: string
  scenario_name: string
  /**
   * Human-readable one-line description of why this alert fired
   * (e.g. "OFAC SDN onboarding match — Richard Izuchukwu Uzuh…").
   * Mirrors `entities.Alert.Summary` (`internal/entities/alert.go`)
   * via `AlertView.Summary` (`internal/api/alerts_handler.go`) — the
   * backend field has no `omitempty`, so it is always present on the
   * wire, but may be an empty string: today's primary write site is
   * a manual PATCH handler, not universal at alert-creation time.
   * Declared optional here (rather than required non-empty) so
   * pre-existing fixture/test literals across the alerts and cases
   * features keep compiling; treat both `undefined` and `''` as "no
   * summary" when rendering — never synthesize fallback prose from
   * other fields.
   */
  summary?: string
  priority: Priority
  /** 0–100. */
  score: number
  status: AlertStatus
  channel: Channel
  /** Stored as kobo. Null when alert isn't tied to a single transaction. */
  amount_minor: number | null
  customer: { id: string; display_name: string; customer_number: string }
  queue_id: string
  queue_name: string
  assigned_to: Assignee | null
  sla: SlaInfo
  created_at: string
  updated_at: string
  /** True when sla.deadline_at is in the past and alert is still open. */
  sla_breached: boolean
  /** Set when this alert has been grouped into a case (AF-127). */
  case_id: string | null
  case_number: string | null
  /** Branch chip — derived from the triggering transaction's branch. */
  branch_code?: string
  branch_name?: string
}

/**
 * `true` when `alert.summary` has real content to render. Single
 * source of truth so every callsite (list-row tooltip, detail
 * why-panel) treats `undefined` and `''` identically instead of
 * drifting into ad hoc truthiness checks.
 */
export function hasSummary<T extends { summary?: string }>(
  alert: T,
): alert is T & { summary: string } {
  return typeof alert.summary === 'string' && alert.summary.trim().length > 0
}

export interface AlertNote {
  id: string
  body: string
  author: Assignee
  created_at: string
}

export interface StatusHistoryEntry {
  id: string
  from_status: AlertStatus | null
  to_status: AlertStatus
  by: Assignee | null
  at: string
  reason: string | null
}

export interface RuleHit {
  rule_code: string
  rule_name: string
  /** Boolean signals fired; multiplier-style numeric signals included where present. */
  evidence: Record<string, string | number | boolean>
}

export interface AlertDetail extends AlertSummary {
  /** Reason codes from the scenario engine — short tokens like 'NAME_MISMATCH'. */
  reason_codes: string[]
  rules_fired: RuleHit[]
  /** AF-125 transaction ID; null when alert is customer-level (e.g. profile-change scenarios). */
  transaction_id: string | null
  customer_snapshot: CustomerSnapshot
  related_alert_ids: string[]
  notes: AlertNote[]
  status_history: StatusHistoryEntry[]
  resolution: Resolution | null
  /**
   * Present when this alert was minted by the screening pipeline
   * (one or more screening.matches reference this alert). Drives the
   * "View screening" header chip on the alert detail page so analysts
   * can pivot back to /screening/screenings/{public_id}.
   */
  screening?: AlertScreeningLink | null
  /**
   * Present when this alert was minted by the adverse-media pipeline
   * (an adverse_media.classifications row references this alert via
   * alert_id). Drives the "View adverse media" header chip on the
   * alert detail page so analysts can pivot to
   * /adverse-media/classifications/{public_id}.
   */
  adverse_media?: AlertAdverseMediaLink | null
}

/** Screening provenance for an alert that came from the screening pipeline. */
export interface AlertScreeningLink {
  public_id: string
  list_code: string
  score_band: 'weak' | 'review' | 'block'
  composite_score: number
  entity_name?: string
}

/** Adverse-media provenance for an alert that came from the adverse-media pipeline. */
export interface AlertAdverseMediaLink {
  /** classification public_id — for deep link to /adverse-media/classifications/{id} */
  public_id: string
  /** Risk category from the classification (e.g. 'fraud', 'corruption'). */
  category?: string
  /** none | low | medium | high | critical */
  severity: 'none' | 'low' | 'medium' | 'high' | 'critical'
  /** 0..1 confidence the LLM emitted. */
  confidence: number
  /** Source feed tier (T1..T4) at classification time. */
  source_credibility_tier?: 'T1' | 'T2' | 'T3' | 'T4'
  /** Optional article title for the chip tooltip. */
  article_title?: string
  /** Optional subject name for human context (e.g. 'Aisha Yusuf'). */
  subject_name?: string
}

// ---------- query shape ----------

export type SortDir = 'asc' | 'desc'
export interface SortKey {
  field: keyof AlertSummary | 'score' | 'amount_minor' | 'sla_remaining'
  dir: SortDir
}

export interface AlertFilters {
  status?: AlertStatus[]
  priority?: Priority[]
  scenario?: string[]
  queue_id?: string[]
  channel?: Channel[]
  assigned_to?: string[] | 'unassigned'
  sla_at_risk?: boolean
  sla_breached?: boolean
  amount_min_minor?: number
  amount_max_minor?: number
  date_from?: string
  date_to?: string
  /** Free-text search across alert number + scenario name + customer. */
  q?: string
}

export interface SavedFilter {
  id: string
  name: string
  filters: AlertFilters
  /** Server-computed live count, refreshed via WebSocket. */
  count: number
  /** Built-in sets are not user-deletable. */
  builtin: boolean
}

export interface AlertsListResponse {
  alerts: AlertSummary[]
  total: number
  page: number
  page_size: number
  /** Live counts for the sidebar — keyed by queue_id. */
  queue_depths: Record<string, number>
}

// ---------- mutations ----------

export interface BulkAssignPayload {
  alert_ids: string[]
  user_id: string
}

export interface BulkClosePayload {
  alert_ids: string[]
  resolution: Resolution
  reason?: string
}

/**
 * Mirrors `alerts.MaxBulkCloseSize` in
 * `internal/cases/alerts/service.go`. `BulkClose` takes a Postgres row
 * lock (`GetByIDForUpdate`) per alert, held for the whole transaction —
 * an unbounded batch would hold an unbounded number of write locks
 * across an unbounded number of sequential round trips, so the backend
 * rejects (whole-batch, 400 `invalid_input`) anything over this size.
 *
 * This is CLOSE-SPECIFIC. Bulk assign / escalate / link-to-case take no
 * row locks and share the handler's generic batch cap instead (500 —
 * see `internal/api/alerts_handler.go`'s bulk-request decoder), which
 * this constant must NOT be reused to bound. `BulkActionsBar` disables
 * only the close action above this size; assign/escalate/link-to-case
 * stay unrestricted by it.
 *
 * If `MaxBulkCloseSize` ever changes server-side, update this value in
 * the same PR — a drifted copy here is exactly the bug class this
 * constant exists to prevent (see `closedStatusForResolution`'s doc
 * comment for the last time frontend/backend contract drift silently
 * broke every real bulk-close).
 */
export const MAX_BULK_CLOSE_SIZE = 100

/**
 * Per-alert outcome from `POST /alerts/bulk/close`. Mirrors
 * `BulkCloseResultView` in `internal/api/alerts_handler.go`. `error`
 * is a short client-safe code: `not_found`,
 * `invalid_transition:<from>-><to>`, or `rolled_back` (this alert
 * WOULD have closed, but a peer in the batch failed and the whole
 * batch — whole-batch-rejection — was discarded).
 */
export interface BulkCloseResultView {
  alert_id: string
  success: boolean
  error?: string
  status?: string
}

/**
 * Body of a 207 Multi-Status response from `POST /alerts/bulk/close`
 * (mirrors `BulkCloseResponseView`). `affected` is always 0 on this
 * path — see `AlertsHandler.BulkClose`'s doc comment: the whole batch
 * is rejected together, nothing is partially applied.
 */
export interface BulkCloseFailureBody {
  affected: number
  results: BulkCloseResultView[]
}

/**
 * Narrows an unknown value (typically `ApiError.details` off a 207
 * response — see `api/client.ts`) to `BulkCloseFailureBody` without an
 * `as` cast. `client.ts` doesn't know the endpoint-specific 207 shape,
 * so callers reading `error.details` must validate it themselves.
 */
export function isBulkCloseFailureBody(v: unknown): v is BulkCloseFailureBody {
  if (typeof v !== 'object' || v === null) return false
  const results = (v as { results?: unknown }).results
  return Array.isArray(results)
}

/**
 * Translates a single `BulkCloseResultView.error` code into an
 * operator-facing clause ("is merged into a case", "no longer
 * exists", …). Exhaustive over the transition-failure codes the
 * backend's `closeChain` can produce for a *closed_* target (see
 * `internal/cases/alerts/service.go` — from `open`/`in_review` a
 * legal chain always exists, so `invalid_transition` can only
 * originate from a terminal `from` status, or `escalated` reaching
 * for `closed_duplicate`, the one edge escalated lacks).
 */
function bulkCloseFailureClause(error: string): string {
  if (error === 'not_found') return 'no longer exists'
  const match = /^invalid_transition:([a-z_]+)->/.exec(error)
  if (match) {
    // No cast to `AlertStatus` needed: TS narrows `from` (declared
    // `string` from the regex capture) to the matched case's literal
    // union inside each branch below, and every literal used is a
    // real `AlertStatus` member — `ALERT_STATUS_LABEL[from]` indexes
    // cleanly on that narrowed type.
    const from = match[1]
    switch (from) {
      case 'merged':
        return 'is merged into a case'
      case 'closed_fraud':
      case 'closed_false_positive':
      case 'closed_duplicate':
      case 'closed_other':
      case 'closed':
        return `is already ${ALERT_STATUS_LABEL[from].toLowerCase()}`
      case 'escalated':
        return "can't be closed as a duplicate once escalated"
      case 'open':
      case 'in_review':
        // Should not occur — open/in_review always have a legal chain
        // to any closed_* target — but fail safe with a generic clause
        // instead of a silent gap if the server-side matrix changes.
        return "can't reach that outcome from its current status"
    }
  }
  return 'was blocked'
}

/**
 * Builds an operator-facing title + description for a rejected bulk
 * close. Every alert in the batch was rejected together (see
 * `BulkCloseFailureBody`'s doc comment), so this groups the BLOCKING
 * failures (excludes `rolled_back` entries — those alerts were fine,
 * they were just caught in the same batch) by reason and reports
 * counts. Caps the number of distinct reasons shown so a large,
 * highly-mixed batch doesn't produce an unreadable wall of text.
 */
export function describeBulkCloseRejection(body: BulkCloseFailureBody): {
  title: string
  description: string
} {
  const total = body.results.length
  const title = `Close rejected — 0 of ${total} alert${total === 1 ? '' : 's'} closed`
  const blocking = body.results.filter((r) => !r.success && r.error !== 'rolled_back')

  if (blocking.length === 0) {
    return {
      title,
      description: 'The batch was rejected server-side. Reload and try again.',
    }
  }

  const counts = new Map<string, number>()
  for (const r of blocking) {
    const clause = bulkCloseFailureClause(r.error ?? '')
    counts.set(clause, (counts.get(clause) ?? 0) + 1)
  }
  const MAX_REASONS = 3
  const entries = [...counts.entries()]
  const shown = entries
    .slice(0, MAX_REASONS)
    .map(([clause, count]) => `${count} alert${count === 1 ? '' : 's'} ${clause}`)
  const remainder = entries.length - MAX_REASONS
  const reasons =
    remainder > 0 ? `${shown.join('; ')}; and ${remainder} more reason${remainder === 1 ? '' : 's'}` : shown.join('; ')

  return {
    title,
    description: `Nothing was closed. ${reasons}. Deselect these and retry.`,
  }
}

/**
 * Single-alert close payload — routed through `POST
 * /alerts/{id}/close`, the endpoint that enforces the workflow state
 * machine server-side (`internal/api/alerts_handler.go` `Close`).
 * Distinct from `BulkClosePayload`, which hits `/alerts/bulk/close`
 * for genuine multi-select. The alert-detail rail must always close a
 * single alert through this shape, never through
 * `BulkClosePayload` with a one-element `alert_ids` array.
 */
export interface ClosePayload {
  alert_id: string
  resolution: Resolution
  reason?: string
}

export interface BulkEscalatePayload {
  alert_ids: string[]
  reason: string
}

export interface AddNotePayload {
  alert_id: string
  body: string
}

// ---------- WebSocket events ----------

export type AlertWsEvent =
  | { type: 'alerts.created'; payload: AlertSummary }
  | { type: 'alerts.updated'; payload: AlertSummary }
  | { type: 'alerts.assigned'; payload: { id: string; assigned_to: Assignee | null } }
  | { type: 'alerts.status_changed'; payload: { id: string; status: AlertStatus } }
  | { type: 'alerts.deleted'; payload: { id: string } }

// ---------- additional payloads + entities ----------

export interface BulkLinkToCasePayload {
  alert_ids: string[]
  case_id: string | null
  /** When `case_id` is null, server creates a new case with this title. */
  new_case_title?: string
  /**
   * Optional richer case-creation fields. The wizard surfaces them
   * prefilled from the selected alerts so the analyst doesn't have
   * to re-type what's already on screen. Server uses them when
   * `case_id` is null; ignored on the link-existing branch.
   */
  new_case_summary?: string
  new_case_priority?: 'low' | 'medium' | 'high' | 'critical'
  new_case_queue?: string
}

/**
 * CaseSummary mirrors the slimmed-down list-row shape every cases
 * surface uses. The backend wire response carries more fields (see
 * CaseRichSummary below) — the AlertLinker picker only reads
 * id/title/status/alert_count, so this stays small + the richer
 * page reads through CaseRichSummary instead.
 */
/**
 * Backend wire statuses are `open | in_progress | pending_info |
 * escalated | resolved | closed` (see entities/case.go). The legacy
 * UX-friendly aliases (`in_review`, `fraud_confirmed`,
 * `false_positive`) live alongside them so the cases list / KPI
 * tiles that already key on those values keep working — the panel
 * dispatch + the workflow API consumers must use the wire values
 * instead.
 */
export type CaseWireStatus =
  | 'open'
  | 'in_progress'
  | 'pending_info'
  | 'escalated'
  | 'resolved'
  | 'closed'

export type CaseUiStatus = 'in_review' | 'fraud_confirmed' | 'false_positive'

export interface CaseSummary {
  id: string
  case_number: string
  title: string
  status: CaseWireStatus | CaseUiStatus
  alert_count: number
}

/**
 * CaseRichSummary is the full list-row shape the cases-list page
 * consumes. The backend's GET /cases endpoint returns this — fields
 * are nullable because legacy rows may pre-date the column and the
 * search-result variant carries a `relevance` score the standard
 * list omits.
 */
export interface CaseRichSummary extends CaseSummary {
  priority: 'critical' | 'high' | 'medium' | 'low'
  queue?: string
  summary?: string
  assigned_to_id?: number | null
  // Hydrated by the server: user_id (public_id) + display_name +
  // initials. Older clients still receive the bare assigned_to_id
  // and rendered "user #<id>" — assigned_to is the new field they
  // should bind to when present.
  assigned_to?: Assignee | null
  risk_score?: number | null
  sla_deadline?: string | null
  sla_breached?: boolean
  sla_breached_at?: string | null
  resolved_at?: string | null
  closed_at?: string | null
  created_at: string
  updated_at: string
  relevance?: number | null
}

export interface AnalystSummary {
  user_id: string
  display_name: string
  initials: string
  /** Live workload for the assignment picker — open + in_review counts. */
  open_count: number
}

export interface ExportJob {
  id: string
  status: 'queued' | 'running' | 'ready' | 'failed'
  /** Set when status === 'ready'. Signed URL valid for 5 minutes. */
  download_url: string | null
  /** Set when status === 'failed'. */
  error: string | null
  /** Number of rows in the export. */
  row_count: number
}

export interface CreateSavedFilterPayload {
  name: string
  filters: AlertFilters
}
